import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPrebuildFileReview,
  type FileReviewFile,
  type FileReviewSheet,
  type FileReviewSummaryResponse,
} from "@/lib/api/databook";
import { LoadingState } from "@/components/ui/loading-state";
import { groupFileReview, type FileReviewSheetRow } from "./fileReviewCategories";

interface FileReviewStageProps {
  projectId?: string;
  onContinue: () => void;
}

type UsageStatus = FileReviewSheet["status"];
type SheetFilter = "all" | UsageStatus;
const count = (value: number) => value.toLocaleString("en-GB");
const filters: { value: SheetFilter; label: string }[] = [
  { value: "all", label: "All sheets" },
  { value: "used", label: "Used" },
  { value: "unused", label: "Not used" },
];

function StatusBadge({ status }: { status: UsageStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
      status === "used" ? "bg-emerald-50 text-emerald-800" : "bg-muted text-muted-foreground"
    }`}>
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${status === "used" ? "bg-emerald-600" : "bg-muted-foreground/60"}`} />
      {status === "used" ? "Used" : "Not used"}
    </span>
  );
}

export function FileReviewStage({ projectId, onContinue }: FileReviewStageProps) {
  const review = useQuery({
    queryKey: ["file-review", projectId],
    queryFn: () => getPrebuildFileReview(projectId!),
    enabled: Boolean(projectId),
    staleTime: 0,
  });

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="builder-stage-frame pb-16 pt-8">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold tracking-tight">File review</h1>
          <button
            type="button"
            onClick={onContinue}
            className="shrink-0 rounded-lg bg-foreground px-5 py-3 text-sm font-medium text-background hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Continue
          </button>
        </div>
        {review.isLoading && <LoadingState />}
        {(review.isError || !projectId) && (
          <div role="alert" className="rounded-xl border p-4 text-sm">
            File review could not be loaded.
            {projectId && (
              <button type="button" className="ml-3 underline" onClick={() => void review.refetch()}>
                Retry
              </button>
            )}
          </div>
        )}
        {review.data?.state === "unavailable" && (
          <p className="rounded-xl border p-4 text-sm text-muted-foreground">{review.data.message}</p>
        )}
        {review.data?.state === "loaded" && <FileReviewSummary key={projectId} review={review.data} />}
      </div>
    </div>
  );
}

function SheetRow({ row }: { row: FileReviewSheetRow }) {
  const { filename, sheet } = row;
  return (
    <tr className="border-t border-border/50 align-top">
      <th scope="row" className="px-6 py-4 text-left font-normal">
        <div className="break-words font-medium">{sheet.name}</div>
        <div title={`${filename} › ${sheet.name}`} className="mt-0.5 break-words text-xs text-muted-foreground">
          {filename} › {sheet.name}
        </div>
      </th>
      <td className="px-3 py-4"><StatusBadge status={sheet.status} /></td>
      <td className="break-words px-6 py-4">
        {sheet.status === "used" ? (
          sheet.reports.length > 0 ? (
            <><span className="text-muted-foreground">Feeds </span><span className="font-medium">{sheet.reports.join(", ")}</span></>
          ) : <span className="text-muted-foreground">Report details not available.</span>
        ) : <span className="text-muted-foreground">{sheet.unused_reason?.trim() || "Reason not provided."}</span>}
      </td>
    </tr>
  );
}

function FilesWithoutSheets({ files }: { files: FileReviewFile[] }) {
  if (files.length === 0) return null;
  return (
    <section aria-label="Files without reviewed sheets" className="mt-8">
      <h2 className="text-sm font-semibold">Files without reviewed sheets</h2>
      <p className="mt-1 text-xs text-muted-foreground">These files are not included in the sheet counts or filters.</p>
      <ul className="mt-3 divide-y divide-border/50 rounded-xl border px-6">
        {files.map((file) => (
          <li key={file.document_id} className="flex items-start justify-between gap-4 py-4 text-sm">
            <div className="min-w-0">
              <div className="break-words font-medium">{file.filename}</div>
              <p className="mt-1 text-xs text-muted-foreground">
                {file.source_type === "pdf" ? "PDF documents are counted as used."
                  : file.source_type === "unanalysed" ? "This file was not included in source analysis."
                    : "No analyzed sheets in this file."}
              </p>
            </div>
            <StatusBadge status={file.status} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function FileReviewSummary({ review }: { review: FileReviewSummaryResponse }) {
  const [filter, setFilter] = useState<SheetFilter>("all");
  const categories = groupFileReview(review.files);
  const all = categories.reduce((total, category) => total + category.rows.length, 0);
  const used = categories.reduce((total, category) => total + category.used, 0);
  const counts: Record<SheetFilter, number> = { all, used, unused: all - used };
  const visibleCategories = categories.map((category) => ({
    ...category,
    total: category.rows.length,
    rows: category.rows.filter(({ sheet }) => filter === "all" || sheet.status === filter),
  })).filter((category) => category.rows.length > 0);
  const otherFiles = review.files.filter((file) => file.source_type !== "excel" || file.sheets.length === 0);

  return (
    <>
      <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Each reviewed sheet, whether it went into the databook, and where it was used or why it was not used.
        {review.generated_at && <> Latest build {new Date(review.generated_at).toLocaleString()}.</>}
      </p>
      <div role="group" aria-label="Filter sheets by usage" className="mb-6 mt-7 flex flex-wrap gap-2">
        {filters.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={`rounded-full border px-4 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              filter === value ? "border-foreground bg-foreground font-medium text-background"
                : "border-border bg-background text-foreground hover:bg-muted"
            }`}
          >
            {label} · {count(counts[value])}
          </button>
        ))}
      </div>
      <p role="status" className="sr-only">Showing {count(counts[filter])} of {count(all)} sheets.</p>
      {all === 0 ? (
        <p className="rounded-xl border p-6 text-sm text-muted-foreground">
          {review.files.length === 0 ? "No analyzed source files in this build." : "No reviewed sheets in this build."}
        </p>
      ) : (
        <div role="region" aria-label="Sheet usage by category" tabIndex={0} className="overflow-x-auto rounded-xl border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <table className="w-full min-w-[720px] table-fixed text-sm">
            <caption className="sr-only">Sheet sources, usage decisions and reasons from the latest completed databook build.</caption>
            <colgroup><col className="w-[40%]" /><col className="w-28" /><col /></colgroup>
            <thead className="border-b bg-muted/30 text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-6 py-3 text-left font-medium">Sheet and where it came from</th>
                <th scope="col" className="px-3 py-3 text-left font-medium">Decision</th>
                <th scope="col" className="px-6 py-3 text-left font-medium">Reports / reason</th>
              </tr>
            </thead>
            {visibleCategories.map((category) => (
              <tbody key={category.id}>
                <tr>
                  <th scope="rowgroup" colSpan={3} className="px-6 pb-3 pt-6 text-left">
                    <span className="font-semibold">{category.label}</span>
                    <span className="ml-3 whitespace-nowrap text-xs font-normal text-muted-foreground">
                      {count(category.used)} of {count(category.total)} sheets used
                    </span>
                  </th>
                </tr>
                {category.rows.map((row) => <SheetRow key={JSON.stringify([row.documentId, row.sheet.name])} row={row} />)}
              </tbody>
            ))}
            {visibleCategories.length === 0 && (
              <tbody><tr><td colSpan={3} className="px-6 py-12 text-center text-muted-foreground">No sheets match this filter.</td></tr></tbody>
            )}
          </table>
        </div>
      )}
      <FilesWithoutSheets files={otherFiles} />
    </>
  );
}
